/* eslint react-hooks/exhaustive-deps: "warn" */
import { type ReactNode, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './DefenceSecurityVendorReview.module.scss';

const GROUP_ICONS = {
  shield: <path d="M12 3.5 19 6.5v5.3c0 4.4-3 7.2-7 8.7-4-1.5-7-4.3-7-8.7V6.5z" />,
  lock: (
    <>
      <rect x="5.5" y="10.5" width="13" height="9" rx="2" />
      <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" />
    </>
  ),
  layers: (
    <>
      <path d="M12 3.5 21 8l-9 4.5L3 8z" />
      <path d="M3 13l9 4.5 9-4.5" />
      <path d="M3 17.5 12 22l9-4.5" />
    </>
  ),
} as const;

type Row = { h: string; body: ReactNode };
type Group = { title: string; icon: keyof typeof GROUP_ICONS; rows: Row[] };

type Translate = (key: string) => string;

const getGroups = (t: Translate): Group[] => [
  {
    title: t('Certification and compliance'),
    icon: 'shield',
    rows: [
      {
        h: t('Certification'),
        body: (
          <>
            <b>ISO/IEC 27001</b>{' '}
            {t('certified information security management, independently audited against the standard.')}
          </>
        ),
      },
      {
        h: t('Data protection'),
        body: (
          <>
            {t('UK and EU')} <b>GDPR</b>. {t('Registered with the UK')} <b>Information Commissioner{"'"}s Office</b>.{' '}
            {t('Data processing agreement with')} <b>{t('standard contractual clauses')}</b>{' '}
            {t('and the UK addendum, and a named data protection contact.')}
          </>
        ),
      },
      {
        h: t('Lawful sourcing'),
        body: (
          <>
            {t('Records collected from')} <b>{t('public government registers')}</b>{' '}
            {t('under the terms each registry publishes. Provenance retained at field level.')}
          </>
        ),
      },
    ],
  },
  {
    title: t('Security controls'),
    icon: 'lock',
    rows: [
      {
        h: t('Hosting and residency'),
        body: (
          <>
            <b>EU and UK</b>{' '}
            {t(
              'hosting options. Encrypted in transit and at rest. Residency fixed in the contract rather than handled as an exception.',
            )}
          </>
        ),
      },
      {
        h: t('Access control'),
        body: (
          <>
            {t('Single sign-on over')} <b>SAML</b>
            {t(', role-based permissions, enforced')} <b>{t('multi-factor authentication')}</b>
            {t(', retained access logs.')}
          </>
        ),
      },
      {
        h: t('Availability'),
        body: (
          <>
            {t('Contracted')} <b>{t('uptime target')}</b>{' '}
            {t('with a published status page, and named support contacts for production incidents.')}
          </>
        ),
      },
      {
        h: t('Resilience'),
        body: (
          <>
            {t('Backups, documented')} <b>{t('recovery objectives')}</b>{' '}
            {t('and a restoration procedure that is tested rather than described.')}
          </>
        ),
      },
      {
        h: t('Security testing'),
        body: (
          <>
            {t('Independent')} <b>{t('penetration testing')}</b>{' '}
            {t('on a fixed cadence, with continuous vulnerability management between tests.')}
          </>
        ),
      },
    ],
  },
  {
    title: t('Supply chain and documentation'),
    icon: 'layers',
    rows: [
      {
        h: t('Sub-processors'),
        body: (
          <>
            {t('Published list with')} <b>{t('advance notice')}</b>{' '}
            {t('before any change, so your own vendor register stays accurate.')}
          </>
        ),
      },
      {
        h: t('Vendor pack'),
        body: (
          <>
            {t('Completed')} <b>{t('due diligence questionnaire')}</b>
            {t(', certificates, insurance and financials, released when your assessment opens.')}
          </>
        ),
      },
    ],
  },
];

export const DefenceSecurityVendorReview = () => {
  const { t } = useInt();
  const groups = getGroups(t);
  const ref = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(ref, { threshold: 0.08 });

  return (
    <Section background="#F7F9FD" className={styles.root}>
      <div className={styles.head}>
        <TitleBlock
          title={t('Then it has to clear third-party risk, not just the demo.')}
          description={t('Set out before your security, privacy and procurement teams ask for it.')}
        />
      </div>

      <div className={cn(styles.card, revealed && styles.cardIn)} ref={ref}>
        <div className={styles.tableHead}>
          <span>{t('Control area')}</span>
          <span>{t('What we provide')}</span>
        </div>

        {groups.map((group) => (
          <div className={styles.group} key={group.title}>
            <div className={styles.groupLabel}>
              <span className={styles.groupIcon}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {GROUP_ICONS[group.icon]}
                </svg>
              </span>
              {group.title}
            </div>
            {group.rows.map((row) => (
              <div className={styles.row} key={row.h}>
                <h4 className={styles.rowTitle}>
                  <span className={styles.check}>✓</span>
                  {row.h}
                </h4>
                <p className={styles.rowBody}>{row.body}</p>
              </div>
            ))}
          </div>
        ))}
      </div>
    </Section>
  );
};
